import { Capability } from './capabilities';
import { sessionCan } from './capabilityMatrix';
import { RoleNames } from './roleNames';
import {
  BriqueNames,
  PortailNames,
  RoleSession,
  sessionHasBrique
} from './sessionContext';
import {
  ALL_ROLES,
  RoleVisibilityPolicy,
  resolvePolicy
} from './visibilityPolicy';

/**
 * Accès aux routes (préfixes de pathname) par rôle actif — consommé par RoleGuard.
 * Rôle absent = accès par défaut.
 */
const ROLE_ROUTE_POLICIES: Record<string, RoleVisibilityPolicy> = {
  [RoleNames.Courtier]: { allow: ALL_ROLES, deny: ['/mes-leads-partenaires'] },
  [RoleNames.Plateforme]: { allow: ALL_ROLES, deny: ['/mes-notes', '/mes-statistiques', '/mes-leads', '/mes-leads-partenaires'] },
  [RoleNames.GestionnaireLead]: { allow: ['/mes-leads', '/mes-leads-partenaires', '/mon-compte', '/bord'] }
};

// /mes-leads-partenaires : réservé au Gestionnaire Lead (deny par défaut).
const DEFAULT_ROUTE_POLICY: RoleVisibilityPolicy = { allow: ALL_ROLES, deny: ['/mes-leads-partenaires'] };

const pathMatches = (path: string, prefix: string): boolean =>
  path === prefix || path.startsWith(`${prefix}/`);

/** La route (pathname) est-elle accessible pour ce rôle ? */
export const routeAllowedForRole = (
  roleName: string | null | undefined,
  path: string
): boolean => {
  if (!roleName) return false;
  const policy = resolvePolicy(ROLE_ROUTE_POLICIES, roleName, DEFAULT_ROUTE_POLICY);
  if (policy.deny?.some((prefix) => pathMatches(path, prefix))) return false;
  return (
    policy.allow === ALL_ROLES ||
    policy.allow.some((prefix) => pathMatches(path, prefix))
  );
};

/**
 * La route est-elle accessible pour la SESSION ? Mode portail : Plateforme
 * reprend la politique du rôle Plateforme ; Distribution celle du Courtier,
 * avec /mes-leads-partenaires ouvert par la brique Gestion leads ; Gestion se
 * limite à /validations (brique Valideur), /bord et /mon-compte.
 */
export const routeAllowedForSession = (
  session: RoleSession,
  path: string
): boolean => {
  // Validations : même règle que le menu, la brique Valideur décide quel que
  // soit le portail (la matrice répond non en mode legacy).
  if (pathMatches(path, '/validations')) {
    return sessionCan(session, Capability.ValidationsAccess);
  }

  if (session.mode === 'legacy') {
    return routeAllowedForRole(session.roleName, path);
  }

  // Même règle que le menu (matrice centrale) : les habilitations tunnels
  // exigent la brique Conception d'offre, quel que soit le portail.
  if (pathMatches(path, '/mes-habilitations-tunnels')) {
    return sessionCan(session, Capability.TunnelHabilitationsAccess);
  }

  if (session.portail === PortailNames.Plateforme) {
    return routeAllowedForRole(RoleNames.Plateforme, path);
  }

  if (session.portail === PortailNames.Distribution) {
    if (pathMatches(path, '/mes-leads-partenaires')) {
      return sessionHasBrique(session, BriqueNames.GestionLeads);
    }

    return routeAllowedForRole(RoleNames.Courtier, path);
  }

  // Portail Gestion (minimal) : le tableau de bord et le compte — rien d'autre
  // (les validations sont déjà tranchées plus haut par la matrice).
  if (session.portail === PortailNames.Gestion) {
    return pathMatches(path, '/bord') || pathMatches(path, '/mon-compte');
  }

  return false;
};
